<template>
    <KvModal ref="modal" size="small" @close="reset">
        <template #title>{{ t('clients-manual_automation-title') }}</template>

        <template #body>
            <div v-if="loading" class="d-flex flex-column gap-sm">
                <KvSkeleton height="40px" />
                <KvSkeleton height="40px" />
            </div>

            <div v-else-if="!automations.length" class="text-center">
                <KvTypography color="muted">{{ t('clients-manual_automation-empty') }}</KvTypography>
            </div>

            <div v-else class="d-flex flex-column gap-md">
                <div class="d-flex flex-column gap-sm">
                    <KvTypography weight="medium" color="secondary">
                        {{ t('clients-manual_automation-automation_label') }}
                    </KvTypography>
                    <KvSelect
                        v-model="selectedId"
                        :options="automationOptions"
                        :placeholder="t('clients-manual_automation-automation_placeholder')"
                        full-width
                    />
                </div>

                <div
                    v-for="field in selectedFields"
                    :key="field.key"
                    class="d-flex flex-column gap-sm"
                >
                    <KvTypography weight="medium" color="secondary">
                        <!-- i18n-validator-ignore-next-line: runtime automation field label -->
                        {{ field.label || field.key }}<span v-if="field.required"> *</span>
                    </KvTypography>

                    <KvSwitch
                        v-if="field.type === 'boolean'"
                        :value="!!values[field.key]"
                        @input="setValue(field.key, $event)"
                    />
                    <KvDatePicker
                        v-else-if="field.type === 'date'"
                        :value="values[field.key]"
                        value-format="yyyy-MM-dd"
                        :placeholder="t('clients-manual_automation-date_placeholder')"
                        @input="setValue(field.key, $event)"
                    />
                    <KvInput
                        v-else
                        :type="field.type === 'number' ? 'number' : 'text'"
                        :value="values[field.key]"
                        @input="setValue(field.key, $event)"
                    />
                </div>
            </div>
        </template>

        <template #footer v-if="!loading && automations.length">
            <KvButton variant="outlined" @click="close">
                {{ t('clients-manual_automation-cancel') }}
            </KvButton>
            <KvButton :disabled="!canSubmit || submitting" @click="submit">
                {{ t('clients-manual_automation-confirm') }}
            </KvButton>
        </template>
    </KvModal>
</template>

<script>
import keviApi from "@/keviApi";
import KvButton from "@/components/form/kv-button.vue";
import KvDatePicker from "@/components/inputs/kv-date-picker.vue";
import KvInput from "@/components/inputs/kv-input.vue";
import KvModal from "@/components/modal/modal.vue";
import KvSelect from "@/components/inputs/kv-select.vue";
import KvSkeleton from "@/components/ui/kv-skeleton.vue";
import KvSwitch from "@/components/inputs/kv-switch.vue";
import KvTypography from "@/components/ui/kv-typography.vue";

export default {
    components: {
        KvButton,
        KvDatePicker,
        KvInput,
        KvModal,
        KvSelect,
        KvSkeleton,
        KvSwitch,
        KvTypography,
    },
    props: {
        client: { type: Object, default: null },
    },
    data() {
        return {
            automations: [],
            selectedId: null,
            values: {},
            loading: false,
            submitting: false,
            loaded: false,
        };
    },
    computed: {
        automationOptions() {
            return this.automations.map((automation) => ({
                value: automation.id,
                label: automation.title,
            }));
        },
        selectedAutomation() {
            return this.automations.find((a) => a.id === this.selectedId) || null;
        },
        selectedFields() {
            return this.selectedAutomation ? this.selectedAutomation.fields || [] : [];
        },
        canSubmit() {
            if (!this.selectedAutomation) {
                return false;
            }
            return this.selectedFields.every((field) => {
                if (!field.required) {
                    return true;
                }
                const value = this.values[field.key];
                return value !== undefined && value !== null && value !== "";
            });
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        openModal() {
            this.$refs.modal?.open();
            if (!this.loaded) {
                this.loadAutomations();
            }
        },
        close() {
            this.$refs.modal?.close();
        },
        reset() {
            this.selectedId = null;
            this.values = {};
        },
        // A toggle always has an answer, so booleans start at false instead of
        // being treated as an unfilled required field.
        resetValues() {
            const values = {};
            this.selectedFields.forEach((field) => {
                values[field.key] = field.type === "boolean" ? false : "";
            });
            this.values = values;
        },
        setValue(key, value) {
            this.$set(this.values, key, value);
        },
        loadAutomations() {
            this.loading = true;
            this.$http(keviApi.getOptions("automations/manual")).then(
                (response) => {
                    if (response.status === 200) {
                        this.automations = response.body || [];
                        this.loaded = true;
                    }
                    this.loading = false;
                },
                () => {
                    this.loading = false;
                },
            );
        },
        submit() {
            if (!this.canSubmit || this.submitting) {
                return;
            }
            this.submitting = true;
            const options = keviApi.getOptions("automations/manual/execute", "POST", {
                automation_id: this.selectedId,
                clients_id: this.client.id,
                fields: this.values,
            });
            this.$http(options).then(
                (response) => {
                    this.submitting = false;
                    if (response.status === 200) {
                        this.$store.dispatch(
                            "notification/success",
                            this.t("clients-manual_automation-success"),
                        );
                        this.close();
                        this.$emit("update");
                    } else {
                        this.$store.dispatch(
                            "notification/warning",
                            this.t("clients-manual_automation-error"),
                        );
                    }
                },
                () => {
                    this.submitting = false;
                    this.$store.dispatch(
                        "notification/warning",
                        this.t("clients-manual_automation-error"),
                    );
                },
            );
        },
    },
    watch: {
        selectedId() {
            this.resetValues();
        },
    },
};
</script>
